import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { fetchStudents } from "../../redux/studentSlice";
import { fetchScholarshipDetails } from "../../redux/scholarshipSlice";
import { fetchLeftStudents } from "../../redux/leftStudentsSlice";
import { fetchInactiveStudents } from "../../redux/inactiveStudentsSlice";
import { classList, sectionList, houseList } from "../../utils";
import { formatDate } from "../../functions";
import Select from "react-select";

function StudentReports() {
  const dispatch = useDispatch();

  const { studentDetails, loading: loadingActive } = useSelector(
    (state) => state.students,
  );
  const { data: scholarshipDetails, loading: loadingScholarship } = useSelector(
    (state) => state.scholarship,
  );
  const { data: leftStudents, loading: loadingLeft } = useSelector(
    (state) => state.leftStudents,
  );
  const { data: inactiveStudents, loading: loadingInactive } = useSelector(
    (state) => state.inactiveStudents,
  );

  const [activeDataset, setActiveDataset] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    dispatch(fetchStudents());
    dispatch(fetchScholarshipDetails());
    dispatch(fetchLeftStudents());
    dispatch(fetchInactiveStudents());
  }, [dispatch]);

  // ----------------------- HELPERS -----------------------
  const toUpper = (value) =>
    value !== null && value !== undefined ? String(value).toUpperCase() : "";

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (!acc) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const columnMapping = {
    studentId: "Student ID",
    "studentDetails.admission_no": "Admission No",
    "studentDetails.admissionDate": "Admission Date",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.name": "Student Name",
    "studentDetails.gender": "Gender",
    currentClass: "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.dateOfBirth": "Date of Birth",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.house": "House",
    "studentDetails.category": "Category",
    "studentDetails.religion": "Religion",
    "studentDetails.aadhar_no": "Aadhaar Card No.",
    "studentDetails.bloodGroup": "Blood Group",
    "studentDetails.handicap": "Handicap",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    "parentDetails[0].name": "Father's Name",
    "parentDetails[1].name": "Mother's Name",
    "parentDetails[0].phoneNo": "Father's Phone",
    "parentDetails[0].email": "Email ID",
    "presentAddress.address": "Present Address",
  };

  const formatRowUppercase = (student) => {
    const row = {};
    Object.entries(columnMapping).forEach(([key, header]) => {
      let value = getValueByPath(student, key);
      if (Array.isArray(value)) value = value.join(", ");
      if (
        (key === "studentDetails.dateOfBirth" ||
          key === "studentDetails.admissionDate") &&
        value
      ) {
        value = formatDate(value);
      }
      row[toUpper(header)] = toUpper(value);
    });
    return row;
  };

  const sortByRoll = (arr) =>
    arr.sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

  const writeWorkbook = (workbook, filename) => {
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
    toast.success("Report Generated");
  };

  // ----------------------- DATASET SELECTION -----------------------
  const datasets = {
    active: {
      data: studentDetails,
      label: "Active Students",
      loading: loadingActive,
    },
    scholarship: {
      data: scholarshipDetails,
      label: "Scholarship",
      loading: loadingScholarship,
    },
    left: { data: leftStudents, label: "Left Students", loading: loadingLeft },
    inactive: {
      data: inactiveStudents,
      label: "Inactive Students",
      loading: loadingInactive,
    },
  };

  const currentData = datasets[activeDataset].data || [];
  const currentLoading = datasets[activeDataset].loading;

  // Normalize student records (some have currentClass at root, some inside studentDetails)
  const normalize = (student) => ({
    ...student,
    currentClass:
      student.currentClass || student.studentDetails?.currentClass || "",
  });

  const normalizedData = useMemo(
    () => currentData.map(normalize),
    [currentData],
  );

  const classOptions = useMemo(() => {
    const classes = new Set();
    normalizedData.forEach((s) => {
      if (s.currentClass) classes.add(s.currentClass);
    });
    // Preserve order from classList util, then append any leftovers
    const ordered = classList.filter((c) => classes.has(c));
    classes.forEach((c) => {
      if (!ordered.includes(c)) ordered.push(c);
    });
    return ordered.map((c) => ({ value: c, label: c }));
  }, [normalizedData]);

  const sectionOptions = useMemo(() => {
    if (!selectedClass) return [];
    const sections = new Set();
    normalizedData.forEach((s) => {
      if (
        s.currentClass === selectedClass &&
        s.studentDetails?.currentSection
      ) {
        sections.add(s.studentDetails.currentSection);
      }
    });
    // Preserve order from sectionList util, then append leftovers
    const ordered = sectionList.filter((sec) => sections.has(sec));
    sections.forEach((sec) => {
      if (!ordered.includes(sec)) ordered.push(sec);
    });
    return ordered.map((sec) => ({ value: sec, label: sec }));
  }, [normalizedData, selectedClass]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#ffc107"
        : state.isFocused
          ? "#f8f9fa"
          : "white",
      color: state.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (base) => ({ ...base, color: "#6c757d", fontSize: "14px" }),
    singleValue: (base) => ({ ...base, color: "#212529", fontSize: "14px" }),
  };

  // ----------------------- FILTERS -----------------------
  const filteredData = useMemo(() => {
    let data = [...normalizedData];

    if (selectedClass) {
      data = data.filter((s) => s.currentClass === selectedClass);
    }

    if (selectedSection) {
      data = data.filter(
        (s) => s.studentDetails?.currentSection === selectedSection,
      );
    }

    if (fromDate || toDate) {
      data = data.filter((s) => {
        const adm = s.studentDetails?.admissionDate;
        if (!adm) return false;
        if (fromDate && adm < fromDate) return false;
        if (toDate && adm > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      data = data.filter((s) => {
        const flatten = (obj) => {
          const vals = [];
          for (const k in obj) {
            const v = obj[k];
            if (v && typeof v === "object") vals.push(...flatten(v));
            else if (typeof v === "string") vals.push(v);
          }
          return vals;
        };
        return flatten(s).some((v) => v.toLowerCase().includes(lower));
      });
    }

    return data;
  }, [
    normalizedData,
    selectedClass,
    selectedSection,
    fromDate,
    toDate,
    searchTerm,
  ]);

  // ----------------------- SUMMARY STATS -----------------------
  const summary = useMemo(() => {
    const total = filteredData.length;
    let boys = 0,
      girls = 0,
      transport = 0,
      walker = 0,
      handicap = 0,
      scholarshipCount = 0;

    filteredData.forEach((s) => {
      const g = s.studentDetails?.gender?.toLowerCase();
      if (g === "male") boys++;
      else if (g === "female") girls++;
      if (s.transportFacility?.busStop) transport++;
      else walker++;
      if (s.studentDetails?.handicap?.toLowerCase() === "yes") handicap++;
      if (s.scholarship && s.scholarship.type) scholarshipCount++;
    });

    return {
      total,
      boys,
      girls,
      transport,
      walker,
      handicap,
      scholarshipCount,
    };
  }, [filteredData]);

  // ----------------------- REPORT GENERATORS -----------------------
  const exportFlatReport = (label) => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = filteredData.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, label.slice(0, 31));
    writeWorkbook(workbook, `${label}_${today}.xlsx`);
  };

  const exportGroupWise = (label, groupKeyFn, groupLabelFn = (k) => k) => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const groups = {};

    filteredData.forEach((s) => {
      const key = groupKeyFn(s) || "UNKNOWN";
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatRowUppercase(s));
    });

    Object.entries(groups).forEach(([key, students]) => {
      sortByRoll(students);
      const worksheet = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        toUpper(groupLabelFn(key)).slice(0, 31),
      );
    });

    writeWorkbook(workbook, `${label}_${today}.xlsx`);
  };

  // ---- Specific Reports ----
  const reportAll = () =>
    exportFlatReport(`${datasets[activeDataset].label}_All`);

  const reportClassSectionWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    filteredData.forEach((s) => {
      const cls = s.currentClass || "UNKNOWN";
      const sec = s.studentDetails?.currentSection || "A";
      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];
      classGroups[cls][sec].push(formatRowUppercase(s));
    });

    Object.entries(classGroups).forEach(([cls, sections]) => {
      const combined = [];
      Object.keys(sections)
        .sort()
        .forEach((sec) => {
          const students = sections[sec];
          sortByRoll(students);
          combined.push({ SECTION: `SECTION ${sec}` });
          combined.push(...students);
          combined.push({});
        });
      const worksheet = XLSX.utils.json_to_sheet(combined);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        toUpper(cls).slice(0, 31),
      );
    });

    writeWorkbook(workbook, `ClassSectionWise_${today}.xlsx`);
  };

  const reportGenderWise = () =>
    exportGroupWise("GenderWise", (s) =>
      toUpper(s.studentDetails?.gender || "UNKNOWN"),
    );

  const reportHouseWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const workbook = XLSX.utils.book_new();
    houseList.forEach((house) => {
      const inHouse = filteredData.filter(
        (s) => s.studentDetails?.house === house,
      );
      if (!inHouse.length) return;
      const formatted = inHouse.map(formatRowUppercase);
      sortByRoll(formatted);
      const worksheet = XLSX.utils.json_to_sheet(formatted);
      XLSX.utils.book_append_sheet(workbook, worksheet, toUpper(house));
    });
    writeWorkbook(workbook, `HouseWise_${today}.xlsx`);
  };

  const reportCategoryWise = () =>
    exportGroupWise("CategoryWise", (s) =>
      toUpper(s.studentDetails?.category || "NONE"),
    );

  const reportReligionWise = () =>
    exportGroupWise("ReligionWise", (s) =>
      toUpper(s.studentDetails?.religion || "UNKNOWN"),
    );

  const reportBloodGroupWise = () =>
    exportGroupWise("BloodGroupWise", (s) =>
      toUpper(s.studentDetails?.bloodGroup || "UNKNOWN"),
    );

  const reportBusWise = () => {
    const filtered = filteredData.filter((s) => s.transportFacility?.busNumber);
    if (!filtered.length) {
      toast.error("No transport students found");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const groups = {};
    filtered.forEach((s) => {
      const bus = s.transportFacility?.busNumber || "UNKNOWN";
      if (!groups[bus]) groups[bus] = [];
      groups[bus].push(formatRowUppercase(s));
    });
    Object.entries(groups).forEach(([bus, students]) => {
      sortByRoll(students);
      const worksheet = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        toUpper(bus).slice(0, 31),
      );
    });
    writeWorkbook(workbook, `BusWise_${today}.xlsx`);
  };

  const reportWalkerStudents = () => {
    const walkers = filteredData.filter((s) => !s.transportFacility?.busStop);
    if (!walkers.length) {
      toast.error("No walker students found");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = walkers.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "WALKERS");
    writeWorkbook(workbook, `WalkerStudents_${today}.xlsx`);
  };

  const reportTransportStudents = () => {
    const transport = filteredData.filter((s) => s.transportFacility?.busStop);
    if (!transport.length) {
      toast.error("No transport students found");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = transport.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "TRANSPORT");
    writeWorkbook(workbook, `TransportStudents_${today}.xlsx`);
  };

  const reportBirthdayToday = () => {
    const t = new Date();
    const md = `${String(t.getMonth() + 1).padStart(2, "0")}-${String(
      t.getDate(),
    ).padStart(2, "0")}`;
    const list = filteredData.filter((s) => {
      const dob = s.studentDetails?.dateOfBirth;
      if (!dob) return false;
      const [, m, d] = dob.split("-");
      return `${m}-${d}` === md;
    });
    if (!list.length) {
      toast.error("No birthdays today");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = list.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "BIRTHDAYS");
    writeWorkbook(workbook, `Birthdays_${today}.xlsx`);
  };

  const reportBirthdayMonth = () => {
    const m = String(new Date().getMonth() + 1).padStart(2, "0");
    const list = filteredData.filter((s) => {
      const dob = s.studentDetails?.dateOfBirth;
      if (!dob) return false;
      return dob.split("-")[1] === m;
    });
    if (!list.length) {
      toast.error("No birthdays this month");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = list.map(formatRowUppercase);
    formatted.sort((a, b) => {
      const da = a["DATE OF BIRTH"] || "";
      const db = b["DATE OF BIRTH"] || "";
      return da.localeCompare(db);
    });
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "MONTH BIRTHDAYS");
    writeWorkbook(workbook, `BirthdaysMonth_${today}.xlsx`);
  };

  const reportHandicap = () => {
    const list = filteredData.filter(
      (s) => s.studentDetails?.handicap?.toLowerCase() === "yes",
    );
    if (!list.length) {
      toast.error("No physically disabled students found");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = list.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "HANDICAP");
    writeWorkbook(workbook, `HandicapStudents_${today}.xlsx`);
  };

  const reportMissingAadhaar = () => {
    const list = filteredData.filter(
      (s) =>
        !s.studentDetails?.aadhar_no ||
        String(s.studentDetails.aadhar_no).trim() === "",
    );
    if (!list.length) {
      toast.error("All students have Aadhaar");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = list.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "MISSING AADHAAR");
    writeWorkbook(workbook, `MissingAadhaar_${today}.xlsx`);
  };

  const reportMissingPhone = () => {
    const list = filteredData.filter(
      (s) =>
        !s.studentDetails?.phoneNo ||
        String(s.studentDetails.phoneNo).trim() === "",
    );
    if (!list.length) {
      toast.error("All students have phone numbers");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = list.map(formatRowUppercase);
    sortByRoll(formatted);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "MISSING PHONE");
    writeWorkbook(workbook, `MissingPhone_${today}.xlsx`);
  };

  const reportSessionWise = () =>
    exportGroupWise("SessionWise", (s) => toUpper(s.session || "UNKNOWN"));

  const reportAdmissionDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    if (!filteredData.length) {
      toast.error("No admissions in this date range");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const formatted = filteredData
      .map(formatRowUppercase)
      .sort((a, b) =>
        (a["ADMISSION DATE"] || "").localeCompare(b["ADMISSION DATE"] || ""),
      );
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(workbook, worksheet, "ADMISSIONS");
    writeWorkbook(workbook, `Admissions_${fromDate}_to_${toDate}.xlsx`);
  };

  const reportSummary = () => {
    if (!normalizedData.length) {
      toast.error("No data available");
      return;
    }

    const classGroups = {};
    normalizedData.forEach((s) => {
      const cls = s.currentClass || "UNKNOWN";
      if (!classGroups[cls]) {
        classGroups[cls] = {
          total: 0,
          boys: 0,
          girls: 0,
          ST: 0,
          SC: 0,
          OBC: 0,
          GENERAL: 0,
          HANDICAP: 0,
          NONE: 0,
          transport: 0,
          walker: 0,
        };
      }
      const g = s.studentDetails?.gender?.toLowerCase();
      const cat = (s.studentDetails?.category || "NONE").toUpperCase().trim();
      const valid = ["ST", "SC", "OBC", "GENERAL", "NONE"];
      const handicap = s.studentDetails?.handicap?.toLowerCase() === "yes";
      const hasTransport = !!s.transportFacility?.busStop;

      classGroups[cls].total++;
      if (g === "male") classGroups[cls].boys++;
      if (g === "female") classGroups[cls].girls++;
      if (handicap) classGroups[cls].HANDICAP++;
      if (valid.includes(cat)) classGroups[cls][cat]++;
      else classGroups[cls].NONE++;
      if (hasTransport) classGroups[cls].transport++;
      else classGroups[cls].walker++;
    });

    const rows = Object.entries(classGroups).map(([cls, st]) => ({
      Class: cls,
      Total: st.total,
      Boys: st.boys,
      Girls: st.girls,
      ST: st.ST,
      SC: st.SC,
      OBC: st.OBC,
      General: st.GENERAL,
      Handicap: st.HANDICAP,
      None: st.NONE,
      Transport: st.transport,
      Walker: st.walker,
    }));

    const totals = rows.reduce(
      (acc, r) => {
        Object.keys(acc).forEach((k) => {
          if (k !== "Class") acc[k] += r[k];
        });
        return acc;
      },
      {
        Class: "TOTAL",
        Total: 0,
        Boys: 0,
        Girls: 0,
        ST: 0,
        SC: 0,
        OBC: 0,
        General: 0,
        Handicap: 0,
        None: 0,
        Transport: 0,
        Walker: 0,
      },
    );
    rows.push(totals);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
    writeWorkbook(workbook, `StudentSummary_${today}.xlsx`);
  };

  const reportStrengthComparison = () => {
    const all = {};
    const ingest = (arr, key) => {
      arr.forEach((s) => {
        const cls =
          s.currentClass || s.studentDetails?.currentClass || "UNKNOWN";
        if (!all[cls])
          all[cls] = { Class: cls, Active: 0, Left: 0, Inactive: 0 };
        all[cls][key]++;
      });
    };
    ingest(studentDetails || [], "Active");
    ingest(leftStudents || [], "Left");
    ingest(inactiveStudents || [], "Inactive");

    const rows = Object.values(all);
    if (!rows.length) {
      toast.error("No data available");
      return;
    }

    const totals = rows.reduce(
      (acc, r) => {
        acc.Active += r.Active;
        acc.Left += r.Left;
        acc.Inactive += r.Inactive;
        return acc;
      },
      { Class: "TOTAL", Active: 0, Left: 0, Inactive: 0 },
    );
    rows.push(totals);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Strength Comparison");
    writeWorkbook(workbook, `StrengthComparison_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const master = [];
    const tag = (arr, status) => {
      arr.forEach((s) => {
        const row = formatRowUppercase(normalize(s));
        row["STATUS"] = status;
        master.push(row);
      });
    };
    tag(studentDetails || [], "ACTIVE");
    tag(leftStudents || [], "LEFT");
    tag(inactiveStudents || [], "INACTIVE");

    if (!master.length) {
      toast.error("No data available");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(master);
    XLSX.utils.book_append_sheet(workbook, worksheet, "MASTER");
    writeWorkbook(workbook, `MasterReport_${today}.xlsx`);
  };

  const reportScholarshipTypeWise = () => {
    const data = scholarshipDetails || [];
    if (!data.length) {
      toast.error("No scholarship records");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const groups = {};
    data.forEach((s) => {
      const t = s.scholarship?.type || "UNKNOWN";
      if (!groups[t]) groups[t] = [];
      groups[t].push(formatRowUppercase(normalize(s)));
    });
    Object.entries(groups).forEach(([t, students]) => {
      sortByRoll(students);
      const worksheet = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        toUpper(t).slice(0, 31),
      );
    });
    writeWorkbook(workbook, `ScholarshipTypeWise_${today}.xlsx`);
  };

  // ----------------------- REPORT GROUPS (UI) -----------------------
  const reportCategories = [
    { key: "all", label: "All Reports" },
    { key: "demographic", label: "Demographic" },
    { key: "academic", label: "Academic" },
    { key: "analytics", label: "Analytics" },
    { key: "transport", label: "Transport" },
    { key: "compliance", label: "Compliance" },
  ];

  const reports = [
    // Demographic
    { cat: "demographic", label: "All Records", action: reportAll },
    {
      cat: "demographic",
      label: "Class / Section Wise",
      action: reportClassSectionWise,
    },
    { cat: "demographic", label: "Gender Wise", action: reportGenderWise },
    { cat: "demographic", label: "House Wise", action: reportHouseWise },
    { cat: "demographic", label: "Category Wise", action: reportCategoryWise },
    { cat: "demographic", label: "Religion Wise", action: reportReligionWise },
    {
      cat: "demographic",
      label: "Blood Group Wise",
      action: reportBloodGroupWise,
    },

    // Transport
    {
      cat: "transport",
      label: "Walker Students",
      action: reportWalkerStudents,
    },
    {
      cat: "transport",
      label: "Availing Transport",
      action: reportTransportStudents,
    },
    { cat: "transport", label: "Bus No Wise", action: reportBusWise },

    // Academic
    {
      cat: "academic",
      label: "Session Wise Admission",
      action: reportSessionWise,
    },
    {
      cat: "academic",
      label: "Admission Date Range",
      action: reportAdmissionDateRange,
    },
    { cat: "academic", label: "Birthday Today", action: reportBirthdayToday },
    {
      cat: "academic",
      label: "Birthdays This Month",
      action: reportBirthdayMonth,
    },

    // Compliance
    { cat: "compliance", label: "Physically Disabled", action: reportHandicap },
    {
      cat: "compliance",
      label: "Missing Aadhaar",
      action: reportMissingAadhaar,
    },
    {
      cat: "compliance",
      label: "Missing Phone No",
      action: reportMissingPhone,
    },

    // Analytics
    {
      cat: "analytics",
      label: "Student Summary Report",
      action: reportSummary,
    },
    {
      cat: "analytics",
      label: "Active vs Left vs Inactive",
      action: reportStrengthComparison,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
    {
      cat: "analytics",
      label: "Scholarship Type Wise",
      action: reportScholarshipTypeWise,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ----------------------- RENDER -----------------------
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                {/* HERO HEADER CARD */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-stars me-1"></i> Reports & Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Student Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter & download every student report — all
                          in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(datasets).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const colorMap = {
                            active: "success",
                            scholarship: "warning",
                            left: "danger",
                            inactive: "info",
                          };
                          const color = colorMap[key];
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${color} text-white`
                                    : `bg-${color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${
                                    isActive ? "text-white" : "text-dark"
                                  }`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${
                                    isActive ? "text-white" : "text-dark"
                                  }`}
                                >
                                  {(ds.data?.length || 0).toLocaleString(
                                    "en-IN",
                                  )}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* FILTERS CARD */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedClass("");
                        setSelectedSection("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>
                      Reset
                    </span>
                  </div>

                  {/* Search bar */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search admission no, name, phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class & Section */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class &amp; Section
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) => {
                              setSelectedClass(opt ? opt.value : "");
                              setSelectedSection("");
                            }}
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-bookmark-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            isDisabled={!selectedClass}
                            placeholder={
                              selectedClass
                                ? "All Sections"
                                : "Select class first"
                            }
                            value={
                              selectedSection
                                ? {
                                    value: selectedSection,
                                    label: selectedSection,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSection(opt ? opt.value : "")
                            }
                            options={sectionOptions}
                            styles={selectStyles}
                            noOptionsMessage={() => "No sections available"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Admission Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut Visual */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#0dcaf0"
                            strokeWidth="12"
                            strokeDasharray={`${summary.total ? (summary.boys / summary.total) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${summary.total ? (summary.girls / summary.total) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${summary.total ? -(summary.boys / summary.total) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">Total</p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {summary.total.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stat Grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-gender-male text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Boys
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.boys.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.total
                                ? Math.round(
                                    (summary.boys / summary.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-gender-female text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Girls
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.girls.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.total
                                ? Math.round(
                                    (summary.girls / summary.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bus-front-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Transport
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.transport.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.walker.toLocaleString("en-IN")} walker
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-universal-access text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Specially Abled
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.handicap.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.total
                                ? (
                                    (summary.handicap / summary.total) *
                                    100
                                  ).toFixed(1)
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT - Reports Panel */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports & Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Students
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {datasets[activeDataset].label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category Tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {reportCategories.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report Buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#reportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="reportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="reportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick Action Cards */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Class Wise Stats
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportStrengthComparison}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Comparison
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Active / Left / Inactive
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Combined Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Statuses in One Sheet
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportBirthdayToday}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Birthdays
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Today</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportBirthdayMonth}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Birthdays
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            This Month
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StudentReports;
